import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
    checkClubCoupon, clubPartnerLogin, getClubPartnerHistory, getClubPartnerMe,
    readSession, redeemClubCoupon, writeSession
} from '../../../../services/PublicClubService';
import { clubImageUrl, formatCouponCode } from '../../../../utils/ClubUtils';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import './Club.css';

const tokenKey = (accessUuid) => `club-partner-token:${accessUuid}`;
const formatDateTime = (value) => (value
    ? new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '');
const apiMessage = (error, fallback) => error?.response?.data?.message || fallback;

// Explicação de cada situação que impede o uso, para o atendente saber o que dizer ao cliente.
const BLOCKED = {
    USED: { title: 'Cupom já utilizado', text: (coupon) => `Este cupom foi usado em ${formatDateTime(coupon.usedAt)}.` },
    EXPIRED: { title: 'Cupom vencido', text: (coupon) => `A validade terminou em ${formatDateTime(coupon.expiresAt)}.` },
    CANCELED: { title: 'Cupom cancelado', text: () => 'Este cupom foi retirado de circulação pela empresa.' },
    NOT_FOUND: { title: 'Cupom não encontrado', text: () => 'Confira o código com o cliente e tente de novo.' }
};

/**
 * Tela de validação do parceiro. Entra pelo link e pelo PIN que a empresa enviou, e fica
 * lembrada no aparelho. O atendente digita o código do cliente, confere quem é e a oferta, e
 * confirma o uso: a partir daí o cupom não serve mais.
 */
const ClubPartnerPortalPage = () => {
    const { accessUuid } = useParams();

    const [token, setToken] = useState(() => readSession(tokenKey(accessUuid)));
    const [me, setMe] = useState(null);
    const [history, setHistory] = useState([]);

    const [pin, setPin] = useState('');
    const [code, setCode] = useState('');
    const [result, setResult] = useState(null);
    const [redeemed, setRedeemed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const logout = useCallback(() => {
        writeSession(tokenKey(accessUuid), '');
        setToken('');
        setMe(null);
        setResult(null);
    }, [accessUuid]);

    const loadHistory = useCallback((sessionToken) => {
        getClubPartnerHistory(accessUuid, sessionToken).then(({ data }) => setHistory(data)).catch(() => setHistory([]));
    }, [accessUuid]);

    useEffect(() => {
        if (!token) return;
        getClubPartnerMe(accessUuid, token)
            .then(({ data }) => {
                setMe(data);
                if (data.renewedToken) {
                    writeSession(tokenKey(accessUuid), data.renewedToken);
                    setToken(data.renewedToken);
                }
                loadHistory(data.renewedToken || token);
            })
            // Sessão vencida ou acesso trocado pela empresa: volta para o PIN.
            .catch(() => logout());
    }, [token, accessUuid, logout, loadHistory]);

    const handleLogin = (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        clubPartnerLogin(accessUuid, pin)
            .then(({ data }) => {
                writeSession(tokenKey(accessUuid), data.token);
                setToken(data.token);
                setPin('');
            })
            .catch((err) => setError(apiMessage(err, 'Não foi possível entrar agora.')))
            .finally(() => setLoading(false));
    };

    const handleCheck = (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        setRedeemed(false);
        checkClubCoupon(accessUuid, token, code)
            .then(({ data }) => setResult(data))
            .catch((err) => (err?.response?.status === 401 ? logout() : setError(apiMessage(err, 'Não foi possível conferir o cupom.'))))
            .finally(() => setLoading(false));
    };

    const handleRedeem = () => {
        setLoading(true);
        setError('');
        redeemClubCoupon(accessUuid, token, result.code)
            .then(({ data }) => {
                setResult(data);
                setRedeemed(true);
                loadHistory(token);
            })
            .catch((err) => {
                if (err?.response?.status === 401) logout();
                // Outro balcão pode ter usado o cupom no mesmo instante: confere de novo para mostrar a situação real.
                else {
                    setError(apiMessage(err, 'Não foi possível confirmar o uso.'));
                    checkClubCoupon(accessUuid, token, result.code).then(({ data }) => setResult(data)).catch(() => {});
                }
            })
            .finally(() => setLoading(false));
    };

    const reset = () => {
        setCode('');
        setResult(null);
        setRedeemed(false);
        setError('');
    };

    if (!token || !me) {
        return (
            <div className="club-partner" style={{ '--club-accent': '#fb6a21' }}>
                <main className="club-partner__main club-partner__main--login">
                    <div className="club-partner__brand">
                        <span>Portal do parceiro</span>
                    </div>
                    <div className="club-panel club-panel--center club-partner__login">
                        <div className="club-partner__icon" aria-hidden="true"><i className="fa-solid fa-ticket"></i></div>
                        <span className="club-partner__eyebrow">CLUBE DE PARCEIROS</span>
                        <h1>{token ? 'Abrindo...' : 'Validação de cupons'}</h1>
                        {token ? <Spinner animation="border" /> : (
                            <Form onSubmit={handleLogin}>
                                <p className="club-muted">Confira os cupons dos clientes de forma rápida e segura.</p>
                                <Form.Label htmlFor="partner-pin" className="club-partner__label">PIN de acesso</Form.Label>
                                <Form.Control id="partner-pin" aria-describedby="partner-pin-help" className="club-input club-input--code" inputMode="numeric" autoComplete="off" maxLength={4}
                                    placeholder="0000" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))} autoFocus />
                                <small id="partner-pin-help" className="club-partner__hint">Digite o PIN de 4 números enviado pela empresa.</small>
                                {error && <div className="club-notice mt-3" role="alert">{error}</div>}
                                <Button type="submit" className="club-btn w-100 mt-3" disabled={loading || pin.length < 4}>
                                    {loading ? <Spinner size="sm" animation="border" /> : <>Acessar portal <i className="fa-solid fa-arrow-right ms-2" aria-hidden="true"></i></>}
                                </Button>
                            </Form>
                        )}
                    </div>
                    <p className="club-partner__support"><i className="fa-solid fa-circle-info" aria-hidden="true"></i> Não recebeu seu PIN? Entre em contato com a empresa responsável pelo clube.</p>
                </main>
                <PublicBrandSignature />
            </div>
        );
    }

    const blocked = result && BLOCKED[result.state];
    const logo = clubImageUrl(me.logoPath);

    return (
        <div className="club-partner" style={{ '--club-accent': '#fb6a21' }}>
            <header className="club-partner__bar">
                {logo && <img src={logo} alt="" className="club-avatar" style={{ width: 40, height: 40 }} />}
                <div className="club-partner__identity">
                    <small>PORTAL DO PARCEIRO</small>
                    <strong>{me.partnerName}</strong>
                    <span>Clube {me.companyName}</span>
                </div>
                <button type="button" className="club-link-button" onClick={logout}>Sair</button>
            </header>

            <main className="club-partner__main">
                <section className="club-panel">
                    <div className="club-partner__section-head"><span className="club-partner__section-icon" aria-hidden="true"><i className="fa-solid fa-ticket"></i></span><div><span className="club-partner__eyebrow">CLUBE DE PARCEIROS</span><h2>Validar cupom</h2></div></div>
                    <p className="club-muted">Digite o código apresentado pelo cliente para conferir o benefício.</p>
                    <Form onSubmit={handleCheck}>
                        <Form.Control aria-label="Código do cupom" className="club-input club-input--code" autoCapitalize="characters" autoComplete="off" maxLength={7}
                            placeholder="K7M 2QX" value={code} onChange={(event) => { setCode(event.target.value.toUpperCase()); setResult(null); setRedeemed(false); }} />
                        {error && <div className="club-notice mt-3" role="alert">{error}</div>}
                        <Button type="submit" className="club-btn w-100 mt-3" disabled={loading || code.replace(/[^A-Za-z0-9]/g, '').length < 6}>
                            {loading && !result ? <Spinner size="sm" animation="border" /> : 'Conferir'}
                        </Button>
                    </Form>
                </section>

                {result && result.state === 'VALID' && (
                    <section className="club-result club-result--ok">
                        <i className="fa-solid fa-circle-check"></i>
                        <h3>Cupom válido</h3>
                        <span className="club-result__who">{result.customerName}</span>
                        <p><strong>{result.offerTitle}</strong></p>
                        {result.offerRules && (
                            <ul className="club-offer__rules club-offer__rules--center">
                                {result.offerRules.split('\n').filter(Boolean).map((rule) => <li key={rule}>{rule}</li>)}
                            </ul>
                        )}
                        <p>Vale até {formatDateTime(result.expiresAt)}</p>
                        <Button className="club-btn w-100 mt-2" onClick={handleRedeem} disabled={loading}>
                            {loading ? <Spinner size="sm" animation="border" /> : 'Confirmar uso'}
                        </Button>
                        <small className="club-muted">Confira o nome com um documento do cliente.</small>
                    </section>
                )}

                {result && result.state === 'USED' && redeemed && (
                    <section className="club-result club-result--ok">
                        <i className="fa-solid fa-circle-check"></i>
                        <h3>Uso confirmado</h3>
                        <span className="club-result__who">{result.customerName}</span>
                        <p>{result.offerTitle}</p>
                        <Button variant="light" className="club-btn club-btn--ghost w-100 mt-2" onClick={reset}>Validar outro cupom</Button>
                    </section>
                )}

                {blocked && !redeemed && (
                    <section className="club-result club-result--bad">
                        <i className="fa-solid fa-circle-xmark"></i>
                        <h3>{blocked.title}</h3>
                        <p>{blocked.text(result)}</p>
                        {result.customerName && <p>{result.customerName}, {result.offerTitle}</p>}
                    </section>
                )}

                <section className="club-panel">
                    <div className="club-partner__history-head"><h2>Últimos cupons validados</h2><i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i></div>
                    {history.length === 0
                        ? <p className="club-muted mb-0">Nenhum cupom validado ainda.</p>
                        : (
                            <ul className="club-history">
                                {history.map((item) => (
                                    <li key={`${item.code}-${item.usedAt}`}>
                                        <span>
                                            <strong>{item.customerName}</strong>
                                            <small>{item.offerTitle}</small>
                                        </span>
                                        <small>{formatDateTime(item.usedAt)} <br />{formatCouponCode(item.code)}</small>
                                    </li>
                                ))}
                            </ul>
                        )}
                </section>
            </main>
            <PublicBrandSignature />
        </div>
    );
};

export default ClubPartnerPortalPage;
